import { useEffect, useState } from "react";
import axios from "axios";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import BrandHeader from "@/components/BrandHeader";
import TemplateSetup from "@/components/TemplateSetup";
import ConvertPO from "@/components/ConvertPO";
import History from "@/components/History";
import { FileSpreadsheet, Settings2, History as HistoryIcon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("convert");
  const [historyReloadKey, setHistoryReloadKey] = useState(0);

  const refreshTemplate = async () => {
    try {
      const res = await axios.get(`${API}/template`);
      setTemplate(res.data);
      if (!res.data.exists) {
        setActiveTab("setup");
      }
    } catch (e) {
      toast.error("Could not reach server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshTemplate();
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <BrandHeader template={template} />

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-8 md:mb-10">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 tracking-tight">
            Purchase Order <span className="text-amber-700">Converter</span>
          </h1>
          <p className="mt-4 text-stone-500 text-base md:text-lg max-w-2xl">
            Upload the PO exported from your inventory software. We map every item onto your
            master template and hand back a spreadsheet ready to upload elsewhere.
          </p>
          {template?.exists && (
            <div className="mt-4">
              <Badge
                data-testid="template-status-badge"
                className="bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-50"
              >
                Template loaded · {template.item_count} items
              </Badge>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList
            data-testid="dashboard-tabs"
            className="bg-white border border-stone-200 rounded-xl p-1.5 h-auto shadow-sm"
          >
            <TabsTrigger
              value="convert"
              data-testid="tab-convert"
              className="px-5 py-2.5 rounded-lg font-body font-medium data-[state=active]:bg-amber-700 data-[state=active]:text-white transition-colors duration-200"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Convert PO
            </TabsTrigger>
            <TabsTrigger
              value="setup"
              data-testid="tab-setup"
              className="px-5 py-2.5 rounded-lg font-body font-medium data-[state=active]:bg-amber-700 data-[state=active]:text-white transition-colors duration-200"
            >
              <Settings2 className="w-4 h-4 mr-2" /> Template Setup
            </TabsTrigger>
            <TabsTrigger
              value="history"
              data-testid="tab-history"
              className="px-5 py-2.5 rounded-lg font-body font-medium data-[state=active]:bg-amber-700 data-[state=active]:text-white transition-colors duration-200"
            >
              <HistoryIcon className="w-4 h-4 mr-2" /> History
            </TabsTrigger>
          </TabsList>

          <TabsContent value="convert" className="mt-8">
            {loading ? (
              <SkeletonCard />
            ) : template?.exists ? (
              <ConvertPO
                api={API}
                onConverted={() => setHistoryReloadKey((k) => k + 1)}
              />
            ) : (
              <Card className="border-stone-200 shadow-sm rounded-xl">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl text-stone-900">
                    Set up your template first
                  </CardTitle>
                  <CardDescription className="font-body text-stone-500">
                    You need to upload the master template Excel once before converting POs.
                    Head to the Template Setup tab to get started.
                  </CardDescription>
                </CardHeader>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="setup" className="mt-8">
            <TemplateSetup api={API} template={template} onUploaded={refreshTemplate} />
          </TabsContent>

          <TabsContent value="history" className="mt-8">
            <History api={API} reloadKey={historyReloadKey} />
          </TabsContent>
        </Tabs>
      </main>

      <footer className="max-w-5xl mx-auto px-6 md:px-10 py-8 text-stone-400 text-xs font-body">
        Baked By Ninis · Internal Operations Tool
      </footer>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="h-40 bg-stone-100 rounded-xl" />
    </div>
  );
}
